import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/sonner';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import AnalyticsTracker from '@/components/AnalyticsTracker';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { FavoritesProvider } from '@/contexts/FavoritesContext';
import Home from '@/pages/Home';
import Directory from '@/pages/Directory';
import Events from '@/pages/Events';
import EventsCountry from '@/pages/EventsCountry';
import EventsRegion from '@/pages/EventsRegion';
import Submit from '@/pages/Submit';
import SubmitMeetup from '@/pages/SubmitMeetup';
import About from '@/pages/About';
import HowToAccept from '@/pages/HowToAccept';
import BusinessMapCountry from '@/pages/BusinessMapCountry';
import BusinessMapRegion from '@/pages/BusinessMapRegion';
import Favorites from '@/pages/Favorites';
import AdminLogin from '@/pages/admin/AdminLogin';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminBusinesses from '@/pages/admin/AdminBusinesses';
import MapSettings from '@/pages/admin/MapSettings';
import GeneralSettings from '@/pages/admin/GeneralSettings';
import SocialMediaManagement from '@/pages/admin/SocialMediaManagement';
import POSManagement from '@/pages/admin/POSManagement';
import EventsManagement from '@/pages/admin/EventsManagement';
import ExchangeManagement from '@/pages/admin/ExchangeManagement';
import PendingAccess from '@/pages/admin/PendingAccess';
import UserManagement from '@/pages/admin/UserManagement';
import APIManagement from '@/pages/admin/APIManagement';
import ContactManagement from '@/pages/admin/ContactManagement';
import Contact from '@/pages/Contact';
import Privacy from '@/pages/Privacy';
import NotFound from '@/pages/NotFound';

// Router component that handles auth callback detection
const AppRouter = () => {
  const location = useLocation();
  
  return <AppContent />;
};

// Wrapper component to conditionally render navigation and footer
const AppContent = () => {
  const location = useLocation();
  const isMapPage = location.pathname.startsWith('/map');
  
  return (
    <>
      {/* Analytics tracker for public pages */}
      <AnalyticsTracker />
      
      {/* Hide Navigation on map page for mobile - show only on md+ */}
      <div className={isMapPage ? 'hidden md:block' : ''}>
        <Navigation />
      </div>
      <Routes>
        {/* Public routes */}
        <Route index element={<Home />} />
        <Route path="directory" element={<Directory />} />
        <Route path="events" element={<Events />} />
        <Route path="events/region/:slug" element={<EventsRegion />} />
        <Route path="events/:iso2" element={<EventsCountry />} />
        <Route path="submit" element={<Submit />} />
        <Route path="submit-meetup" element={<SubmitMeetup />} />
        <Route path="about" element={<About />} />
        <Route path="how-to-accept" element={<HowToAccept />} />
        <Route path="contact" element={<Contact />} />
        <Route path="privacy" element={<Privacy />} />
        {/* Map routes */}
        <Route path="map" element={<Navigate to="/map/au" replace />} />
        <Route path="map-nz" element={<Navigate to="/map/nz" replace />} />
        <Route path="map/region/:slug" element={<BusinessMapRegion />} />
        <Route path="map/:iso2" element={<BusinessMapCountry />} />
        <Route path="favorites" element={<Favorites />} />
        
        {/* Admin routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/callback" element={<Navigate to="/admin/login" replace />} />
        <Route path="/admin/pending" element={<PendingAccess />} />
        <Route path="/admin/users" element={<UserManagement />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/businesses" element={<AdminBusinesses />} />
        <Route path="/admin/map-settings" element={<MapSettings />} />
        <Route path="/admin/settings" element={<GeneralSettings />} />
        <Route path="/admin/general-settings" element={<GeneralSettings />} />
        <Route path="/admin/social-media" element={<SocialMediaManagement />} />
        <Route path="/admin/pos" element={<POSManagement />} />
        <Route path="/admin/events" element={<EventsManagement />} />
        <Route path="/admin/exchanges" element={<ExchangeManagement />} />
        <Route path="/admin/api" element={<APIManagement />} />
        <Route path="/admin/pending-review" element={<Navigate to="/admin/businesses" replace />} />
        <Route path="/admin/contacts" element={<ContactManagement />} />

        {/* Catch-all: unknown routes get a real 404 page, not a blank shell */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isMapPage && <Footer />}
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <FavoritesProvider>
          <div className="App min-h-screen bg-background text-foreground">
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
            <Toaster position="top-right" />
          </div>
        </FavoritesProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
